
import { computed, defineComponent, markRaw, PropType } from 'vue';
import { useStore } from 'vuex';
import { Actions, Getters, store, mlsStore, Mutations, userSettingsStore, formFieldsStore } from '@/store';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import GridRowCellData from '@/models/grid/GridRowCellData';
import GridColumnUtils from '@/utils/GridColumnUtils';
import utils from '@/utils/QSUtils';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import PopoverService from '@/modules/popovers/services/popoverService';
import IMLSPropertySource from '@/models/mls/IMLSPropertySource';
import _ from 'lodash';
import BootstrapHelper from '@/utils/bootstrapHelpers';
import GuideManager from '@/manager/GuideManager';
import ModalConfirmation from '@/components/modals/Confirmation.vue';
import ModalSuggestFormattingRule from '@/components/2.6/modals/SuggestFormattingRule.vue';
import ITransferComparisonValue from '@/models/TransferComparisonValue';
import IFormFieldRule from '@/models/form-field/IFormFieldRule';
import FieldRulesManager from '@/manager/2.6/FieldRulesManager';
import Notes from '@/modules/notes/components/notes.vue';
import NotesService from '@/modules/notes/services/scripts';
import { newUpsertCompDataRequestByFieldName } from '@/store/2.6/models/IUpsertCompDataRequest';
import FormsManager from '@/manager/2.6/FormsManager';

export default defineComponent({
    components: {
        ModalConfirmation,
        ModalSuggestFormattingRule,
        Notes,
    },
    props: {
        cellData: {
            type: Object as PropType<GridRowCellData>,
            required: true,
        },
        fieldId: {
            type: Number,
            required: true,
        },
        fieldName: {
            type: String,
            required: true,
        },
        fieldFriendlyName: {
            type: String,
            required: true,
        },
        photoRow: {
            type: Boolean,
            required: true,
        },
    },
    data(props) {
        return {
            MLSPopover: null,
            NotesPopover: null,
            cellDataClone: props.cellData,
            isEditing: false,
            editValue: '',
            cellId: utils.ranStr(10, 'lower'),
            timer: 0,
            modalConfirmation: {
                show: false,
                component: markRaw(ModalConfirmation),
                message: '',
                size: '',
                buttons: [] as any,
                title: '',
            },
            modalSuggestFormattingRule: {
                show: false,
                component: markRaw(ModalSuggestFormattingRule),
                originalValue: '',
                newValue: '',
            },
        };
    },
    setup() {
        const store = useStore();

        const dataPreferenceOverrides = {} as any;

        return { store, dataPreferenceOverrides };
    },
    watch: {
        cellData(cellData) {
            this.cellDataClone = cellData;
        },
    },
    computed: {
        isFieldData(): boolean {
            return this.cellData.initials === SourceDataInitials.FieldData;
        },
        isTransferData(): boolean {
            return this.cellData.initials === SourceDataInitials.TransferData;
        },
        isCascadeData(): boolean {
            return this.cellData.initials === SourceDataInitials.CascadeData;
        },
        isPhotoRow(): boolean {
            return this.photoRow;
        },
    },
    methods: {
        showAdditionalFields() {
            (this as any).eventBus.emit('launchAdditionalFieldsModal');
        },
        launchDataPreferenceSettingsModal() {
            (this as any).eventBus.emit('launchDataPreferenceSettingsModal', {
                defaultTabIdx: 0,
                fieldId: this.fieldId,
            });
        },
        launchFieldRulesModal() {
            (this as any).eventBus.emit('launchFieldRulesModal', {
                defaultTabIdx: 1,
                fieldId: this.fieldId,
            });
        },
        photoClick() {
            (this as any).eventBus.emit('launchImageViewerModal', {
                imagePath: this.cellData.value,
            });
        },
        hasCompPhoto(): boolean {
            if (this.cellData.value && (this.cellData.value.includes('http') || this.cellData.value.includes('data:image'))) {
                return true;
            }
            return false;
        },
        async startEditingValue() {
            if (this.isEditing) {
                return;
            }

            this.isEditing = true;
            this.editValue = this.cellDataClone.value;

            await utils.sleep(100);

            const inputEl = document.querySelector<HTMLInputElement>(`.ut-edit[data-id="${this.cellId}"]`);
            inputEl?.focus();
            inputEl?.setSelectionRange(this.cellDataClone.value.length, this.cellDataClone.value.length);
        },
        async doneEditingValue(value: string) {
            if (!this.isEditing) {
                return;
            }

            this.isEditing = false;

            const sourceInitial = SourceDataInitials.UserOverride;
            const originalValue = this.cellDataClone.value.trim();
            value = value.trim();

            // console.log(`GridRowCell.doneEditingValue(), Value was[${originalValue}] and is[${value}] on [${this.fieldId},${this.fieldName}].`);

            if (originalValue !== value) {
                const fieldRule = await this.runRulesChecker(value);
                if (fieldRule.fieldRulesApplied) {
                    value = fieldRule.value;
                }
                this.updateActiveCompSourceData(value, sourceInitial, fieldRule.fieldRulesApplied ?? false, undefined);

                const suggestRule = this.cellDataClone.suggestFieldRule;
                if (suggestRule) {
                    const comparison: ITransferComparisonValue = GridColumnUtils.transferValueComparison(suggestRule, originalValue, value);
                    if (!utils.isObjectEmpty(comparison)) {
                        this.modalSuggestFormattingRule.show = true;
                        this.modalSuggestFormattingRule.originalValue = comparison?.changedText?.originalValue || originalValue;
                        this.modalSuggestFormattingRule.newValue = comparison?.changedText?.newValue || value;
                    }
                }
            }
        },
        getInitialName(initial: string) {
            const initialDetails = utils.initialDetails(initial.toLowerCase());
            return initialDetails.title;
        },
        async runRulesChecker(value: string) {
            // Run Rules Checker
            const fieldRule: IFormFieldRule = {
                fieldId: this.fieldId,
                value: value,
            };

            // console.log(`GridRowCell.runRulesChecker(), Begin run field rule on [${this.fieldId},${this.fieldName}] on active comp.`);

            const fieldRules = await FieldRulesManager.runFieldRule(fieldRule);
            if (fieldRules && fieldRules.length > 0) {
                return fieldRules[0];
            }

            fieldRule.fieldRulesApplied = false;
            return fieldRule;
        },
        async updateActiveCompSourceData(value: string, sourceInitial: string, fieldRulesApplied: boolean, sourceInitialIdx: number | undefined) {
            const fieldData = await FormsManager.getFieldDataById(this.fieldId);
            const comp = computed(() => store.getters[Getters.CompAddresses.ActiveComp]);

            const request = newUpsertCompDataRequestByFieldName(comp.value, fieldData?.fieldName ?? '');
            request.value = value;
            request.sourceInitial = sourceInitial.toLowerCase();
            request.fieldRulesApplied = fieldRulesApplied;
            request.sourceInitialIdx = sourceInitialIdx;
            request.description = 'GridRowCell.updateActiveCompSourceData()';
            store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldName, request);
        },
        async selectedValue(value: string, sourceInitial: string, field: string, fieldFriendlyName: string, sourceInitialIdx: number | undefined) {
            const suggest = computed(() => userSettingsStore.SuggestDataPreferenceUpdate);
            const fieldAndSource = field + sourceInitial.toLowerCase();

            const sourceInitialDetails = utils.initialDetails(sourceInitial);
            if (this.saveClick(fieldAndSource) === 3 && suggest.value) this.updatePrefsModal(fieldFriendlyName, sourceInitialDetails);
            GuideManager.guideCompleted(4);

            // If user toggled selected value then unselect value and set sourceInitial to be user override
            if (this.cellDataClone.dataSelected) {
                value = '';
                sourceInitial = SourceDataInitials.UserOverride;
                sourceInitialIdx = undefined;
            }

            // Skip field rule check if no rules exist.
            const fieldData = await FormsManager.getFieldDataById(this.fieldId);

            let fieldRulesApplied = false;

            if (fieldData?.hasFormattingRules) {
                const fieldRule = await this.runRulesChecker(value);
                if (fieldRule.fieldRulesApplied) {
                    value = fieldRule.value;
                }

                fieldRulesApplied = fieldRule.fieldRulesApplied ?? false;
            }

            this.updateActiveCompSourceData(value, sourceInitial, fieldRulesApplied, sourceInitialIdx);
        },
        saveClick(fieldAndSource: string): number {
            if (theLocalStorageUtils.hasKey('dataPreferenceOverrides')) {
                this.dataPreferenceOverrides = theLocalStorageUtils.get('dataPreferenceOverrides');
            } else {
                theLocalStorageUtils.save('dataPreferenceOverrides', '');
            }
            if (utils.isKeyInObject(this.dataPreferenceOverrides, fieldAndSource)) {
                this.dataPreferenceOverrides[fieldAndSource] = Number(this.dataPreferenceOverrides[fieldAndSource]) + 1;
            } else {
                this.dataPreferenceOverrides[fieldAndSource] = 1;
            }

            theLocalStorageUtils.save('dataPreferenceOverrides', JSON.stringify(this.dataPreferenceOverrides));
            return this.dataPreferenceOverrides[fieldAndSource];
        },
        updatePrefsModal(fieldFriendlyName: string, sourceInitialDetails: any) {
            this.modalConfirmation.show = true;
            this.modalConfirmation.title = `Update Data Preference for ${fieldFriendlyName}`;
            this.modalConfirmation.message = `<div>
                                                <p>You changed <b>${fieldFriendlyName}</b> to use data from <b>${sourceInitialDetails.title}</b> three times. Would you like to always use ${sourceInitialDetails.title} data for this field, if available?</p>
                                                <p>QuickSource saves you the most time when you set your data preferences for each field. You can do this in the Data Preferences column or by clicking <i>Review Data Preferences</i> below.</p>
                                                <p><label><input type="checkbox" name="doNotPromptDataPreference" class="me-2">Do not suggest changes to my Data Preferences</label></p>
                                              </div>`;

            this.modalConfirmation.buttons = [
                {
                    label: 'Review Data Preferences',
                    css: 'btn-outline-secondary',
                    callback: () => {
                        this.updateDoNotShowSetting();
                        this.launchDataPreferenceSettingsModal();
                    },
                },
                {
                    label: 'Save',
                    css: 'btn-primary',
                    callback: async () => {
                        const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode);
                        const result = await FormsManager.getFieldDataById(this.fieldId);
                        const obj = GridColumnUtils.parseFlattenedDataPriorities(result?.flattenedDataPriorities ?? '');
                        const idx = obj.findIndex((x: { initials: { toString: () => string } }) => x.initials === sourceInitialDetails.initials);
                        if (idx !== -1) {
                            obj[idx].order = 0;
                        }

                        await formFieldsStore.saveDataPreferenceSourceOrder({
                            fieldId: this.fieldId,
                            dataPreferenceSourceOrder: obj,
                            formCode: formCode,
                        });

                        this.updateDoNotShowSetting();
                    },
                },
                {
                    label: 'Close',
                    css: 'btn-outline-secondary',
                    callback: () => {
                        this.updateDoNotShowSetting();
                    },
                },
            ];
        },
        updateDoNotShowSetting() {
            const doNotPromptDataPreference = document.querySelector('input[name=doNotPromptDataPreference]') as HTMLInputElement;
            userSettingsStore.setSuggestDataPreferenceUpdate({ SuggestDataPreferenceUpdate: !doNotPromptDataPreference.checked });
        },
        getFieldLabelName(fieldName: string) {
            return `CM_OTHR${fieldName.replace(/[^0-9]/g, '')}_TEXT`;
        },
        additionalFieldName(event: any, fieldName: string) {
            clearTimeout(this.timer);
            this.timer = window.setTimeout(() => {
                this.store.dispatch(Actions.CompAddresses.UpdateAdditionalField, {
                    fieldName: fieldName,
                    value: event.target.value,
                });
            }, 500);
        },
        getValue(value: string) {
            const getterComps = store.getters[Getters.CompAddresses.Comps];
            const fieldNameLabel = this.getFieldLabelName(value);
            const itemIdx = getterComps[0].sourceData.findIndex((x: { fieldName: { toString: () => any } }) => x.fieldName === fieldNameLabel);
            let retVal = '';
            if (itemIdx !== -1) {
                retVal = getterComps[0].sourceData[itemIdx].value;
            }
            return retVal;
        },
        getMLSPropertySource(fieldName: string): IMLSPropertySource[] | null | undefined {
            const activeComp = computed(() => store.getters[Getters.CompAddresses.ActiveComp]);
            let mlsPropertySources = computed(() => mlsStore.MlsPropertySourceByAddressAndFormFieldName(activeComp.value.address, fieldName));

            if (!mlsPropertySources.value) {
                mlsPropertySources = computed(() => mlsStore.MlsPropertySourceByAddressAndParentFormFieldName(activeComp.value.address, fieldName));
            }
            return mlsPropertySources.value;
        },
        hasMLSPropertySource(fieldName: string): boolean {
            const result = _.cloneDeep(this.getMLSPropertySource(fieldName)) || [];
            if (result.length > 0) {
                return true;
            }
            return false;
        },
        showMLSPropertySource(event: any, fieldName: string) {
            if (this.MLSPopover) {
                PopoverService.hide(this.MLSPopover);
            }

            PopoverService.hideAll();
            this.MLSPopover = null;

            // Popover
            const options = {
                html: true,
                sanitize: false,
                content: this.generateMLSPropertySourceHTML(_.cloneDeep(this.getMLSPropertySource(fieldName)) || []),
                placement: 'right', // Needs to load to the "right" because the expand method causes positioning issues
                customClass: 'mls-popover',
            };

            this.MLSPopover = PopoverService.initialize(event.target, options);
            PopoverService.open(this.MLSPopover);
            const el = PopoverService.getPopoverElement(this.MLSPopover);

            // On open listeners
            PopoverService.onOpen(this.MLSPopover, async () => {
                const mlsExpand = el.getElementsByClassName('mls-expand')[0];
                const mlsCompress = el.getElementsByClassName('mls-compress')[0];
                const mlsBody = el.getElementsByClassName('pop-body')[0];

                if (utils.checkOverflowX(mlsBody) || utils.checkOverflowY(mlsBody)) {
                    mlsExpand.removeAttribute('hidden');
                }

                // Expand button
                el.getElementsByClassName('mls-expand')[0].addEventListener('click', () => {
                    mlsBody.style.width = 400 + 'px';
                    mlsBody.style.height = 400 + 'px';
                    mlsCompress.removeAttribute('hidden');
                    mlsExpand.setAttribute('hidden', true);
                });

                // Compress button
                el.getElementsByClassName('mls-compress')[0].addEventListener('click', () => {
                    mlsBody.style.width = 275 + 'px';
                    mlsBody.style.height = 100 + 'px';
                    mlsExpand.removeAttribute('hidden');
                    mlsCompress.setAttribute('hidden', true);
                });

                // Close button
                el.getElementsByClassName('mls-close')[0].addEventListener('click', () => {
                    PopoverService.hide(this.MLSPopover);
                    this.MLSPopover = null;
                });
            });
        },
        generateMLSPropertySourceHTML(result: any): string {
            let html = `<div class="pop-container">`;

            // Header
            html += `<div class="pop-header mb-2">
                            <div class="fw-bold">Additional MLS Data</div>
                            <div class="popover-actions">
                                <span class="mls-compress hover pe-1" hidden><i class="fas fa-compress-alt"></i></span>
                                <span class="mls-expand hover pe-1" hidden><i class="fas fa-expand-alt"></i></span>
                                <span class="mls-close hover"><i class="fas fa-times"></i></span>
                            </div>
                       </div>`;

            // Body
            html += `<div class="pop-body">`;
            const mlsProcessedHeaderIdx: number[] = [];
            for (const item in result) {
                if (utils.isValueInArray(mlsProcessedHeaderIdx, result[item]['headerIdx'])) continue;
                mlsProcessedHeaderIdx.push(result[item]['headerIdx']);

                const psValue = result[item]['val'] ? result[item]['val'] : 'N/A';
                html += `<div>${result[item]['headers']}: ${psValue} </div>`;
            }
            html += '</div>';

            html += '</div>';

            return html;
        },
        showNotesPopover(event: any, fieldName: string, friendlyFieldName: string, notes: string) {
            if (this.NotesPopover) {
                PopoverService.hide(this.NotesPopover);
            }

            PopoverService.hideAll();
            this.NotesPopover = null;

            // Popover
            const options = {
                html: true,
                sanitize: false,
                content: this.generateNotesHTML(friendlyFieldName, notes),
                placement: 'right',
                customClass: 'notes-popover',
            };

            this.NotesPopover = PopoverService.initialize(event.target, options);
            PopoverService.open(this.NotesPopover);
            const el = PopoverService.getPopoverElement(this.NotesPopover);

            // On open listeners
            PopoverService.onOpen(this.NotesPopover, async () => {
                BootstrapHelper.tooltipInitialize();

                // Save button
                el.getElementsByClassName('notes-save')[0].addEventListener('click', () => {
                    const value = el.getElementsByClassName('notes-area')[0].value;

                    if (value.length > 256) {
                        return;
                    }

                    store.dispatch(Actions.CompAddresses.UpdateInsertNotes, { fieldName: fieldName, value: value });

                    PopoverService.hide(this.NotesPopover);
                    this.NotesPopover = null;
                });

                // Close button
                el.getElementsByClassName('notes-close')[0].addEventListener('click', () => {
                    PopoverService.hide(this.NotesPopover);
                    this.NotesPopover = null;
                });

                // Textarea
                el.getElementsByClassName('notes-area')[0].addEventListener('keyup', (e: any) => {
                    const notesDetails = NotesService.formatNotes({
                        notesMultiLines: false,
                        notesLimit: 256,
                        notesValue: e.target.value,
                    });

                    const notesContainer = e.target.closest('.notes-container');
                    const count = notesContainer.getElementsByClassName('notes-count')[0];
                    const countError = notesContainer.getElementsByClassName('notes-error')[0];
                    const textarea = notesContainer.getElementsByClassName('notes-area')[0];

                    if (notesDetails.notesError) {
                        count.classList.add('text-red');
                        countError.removeAttribute('hidden');
                    } else {
                        count.classList.remove('text-red');
                        countError.setAttribute('hidden', 'true');
                    }

                    textarea.value = notesDetails.notesValue;
                    count.innerHTML = notesDetails.notesValue.length;
                });
            });
        },
        generateNotesHTML(friendlyFieldName: string, notes: string) {
            let html = `<div class="notes-container">`;

            // Header
            html += `<div class="pop-header mb-2">
                            <div class="fw-bold">${friendlyFieldName} Notes</div>
                            <div class="popover-actions">
                                <span class="hover" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Notes will be saved to the report and can be accessed within E&O." aria-label="Notes will be saved to the report and can be accessed within E&O."><i class="fas fa-info-circle"></i></span>
                            </div>
                       </div>`;

            // Body
            html += `<div class="pop-body">`;
            html += `<textarea class="notes-area">${notes}</textarea>`;

            html += `<div class="container p-0">
                      <div class="row">
                      	<div class="col align-self-center">
                    		<div class="notes-counts fw-bold">
                    			<span class="notes-count">${notes.length}</span> / <span class="notes-limit">256</span>
                    			<span hidden class="notes-error text-red" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="You have exceeded the 256 character limit for notes." aria-label="You have exceeded the 256 character limit for notes."><i class="fas fa-exclamation-triangle"></i></span>
                    		</div>
                    	</div>
                    	<div class="col text-end">
                    		<div class="btn btn-sm btn-primary notes-save">Save</div>
                    		<div class="btn btn-sm btn-outline-secondary notes-close">Cancel</div>
                    	</div>
                      </div>
                    </div>`;

            html += '</div>';

            html += '</div>';

            return html;
        },
    },
});
